// src/components/module/ventas/PaymentModal.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useContext } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Slide,
  Divider,
  Stack,
  alpha,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import { PagoVenta } from "./PagoVenta";
import { formatMoney } from "../../../utils/format";
import { DarkModeContext } from "../../../context/darkModeContext";

const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
  },
};

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  idVenta: number;
  totalAPagar: number;
  idSucursal: number;
  cliente?: { id: number; nombre: string };
  numeroFactura?: string;
  onPagar: (d: { IdVenta: number; IdTipoPago: number; Abono: number; Observacion: string }) => Promise<void>;
  onPagoCompletado: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PaymentModal: React.FC<PaymentModalProps> = ({
  open, onClose, idVenta, totalAPagar, idSucursal,
  cliente, numeroFactura, onPagar, onPagoCompletado,
}) => {
  const themeV = useTheme();
  const fullScreen = useMediaQuery(themeV.breakpoints.down("md"));
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  return (
    <Dialog
        open={open} onClose={onClose} fullScreen={fullScreen}
        maxWidth="sm" fullWidth TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : "14px",
            border: fullScreen ? "none" : `1px solid ${p.border}`,
            bgcolor: p.bg, overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: `1px solid ${p.border}`,
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", bgcolor: p.accentBg,
              display: "flex", alignItems: "center", justifyContent: "center", color: p.accent,
            }}>
              <PaymentIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text }}>
                Procesar Pago
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: p.textT }}>
                Complete la información del pago
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{
            width: 32, height: 32, borderRadius: "8px",
            color: p.textT, "&:hover": { bgcolor: p.bgT },
          }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
          {/* Info de la venta */}
          {cliente && (
            <Box sx={{
              mb: 3, p: 2.5, borderRadius: "10px",
              border: `1px solid ${p.border}`, bgcolor: p.bg,
            }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: numeroFactura ? 2 : 0 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: "8px", bgcolor: p.accentBg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.88rem", fontWeight: 700, color: p.accent,
                }}>
                  {cliente.nombre[0]}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Cliente
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.text }}>
                    {cliente.nombre}
                  </Typography>
                </Box>
              </Stack>

              {numeroFactura && (
                <>
                  <Divider sx={{ borderColor: p.border, my: 1.5 }} />
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.5,
                      px: 1.2, py: 0.3, borderRadius: "6px",
                      bgcolor: p.accentBg, color: p.accent,
                      fontSize: "0.78rem", fontWeight: 600,
                    }}>
                      <ReceiptIcon sx={{ fontSize: 14 }} />
                      Factura N° {numeroFactura}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" }}>Total</Typography>
                      <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {formatMoney(totalAPagar)}
                      </Typography>
                    </Box>
                  </Stack>
                </>
              )}
            </Box>
          )}

          {/* Componente de pago */}
          <PagoVenta
            idVenta={idVenta}
            totalAPagar={totalAPagar}
            idSucursal={idSucursal}
            onPagar={onPagar}
            onPagoCompletado={() => {
              onPagoCompletado();
              onClose();
            }}
          />
        </DialogContent>
      </Dialog>
  );
};

export default PaymentModal;
